/**
 * CandidateDocumentsDisplay Component
 * 
 * Reusable component to display candidate documents in a consistent format.
 * Always visible regardless of document availability.
 * Shows placeholder text when no documents exist.
 * 
 * Used in:
 * - ApplicationDetailsPage (main details view)
 * - Workflow sections (Interview, Offer, Payment, etc.)
 */
import React, { useState } from 'react';

const CandidateDocumentsDisplay = ({ candidate, compact = false }) => {
  const [documentPreview, setDocumentPreview] = useState({
    isOpen: false,
    title: '',
    rawUrl: '',
    previewUrl: '',
    previewType: 'unsupported'
  });

  // Helper function to check if any documents exist
  const hasAnyDocuments = () => {
    return !!(
      candidate?.documents?.resume?.url ||
      candidate?.resumeUrl ||
      candidate?.documents?.combinedDocuments?.url ||
      (candidate?.additionalDocuments && candidate.additionalDocuments.length > 0)
    );
  };

  const buildDocumentPreview = (url, filename) => {
    const lowerName = String(filename || '').toLowerCase();
    const lowerUrl = String(url || '').toLowerCase();

    const isPdf = lowerName.endsWith('.pdf') || lowerUrl.includes('.pdf');
    const isImage = ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => 
      lowerName.endsWith(ext) || lowerUrl.includes(ext)
    );
    const isDoc = lowerName.endsWith('.doc') || lowerName.endsWith('.docx') || 
                  lowerUrl.includes('.doc') || lowerUrl.includes('.docx');

    if (isPdf) {
      return { previewType: 'pdf', previewUrl: url };
    }

    if (isImage) {
      return { previewType: 'image', previewUrl: url };
    }

    if (isDoc) {
      return {
        previewType: 'office',
        previewUrl: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
      };
    }

    return { previewType: 'unsupported', previewUrl: '' };
  };

  const openDocumentPreview = (url, filename) => {
    if (!url) return;

    const preview = buildDocumentPreview(url, filename);

    setDocumentPreview({
      isOpen: true,
      title: filename || 'Document Preview',
      rawUrl: url,
      previewUrl: preview.previewUrl,
      previewType: preview.previewType
    });
  };

  const closeDocumentPreview = () => {
    setDocumentPreview({
      isOpen: false,
      title: '',
      rawUrl: '',
      previewUrl: '',
      previewType: 'unsupported'
    });
  };

  if (!candidate) {
    return null;
  }

  return (
    <>
      {/* Documents Section - Always visible */}
      <div className={`bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)] ${compact ? 'mb-4' : ''}`}>
        <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">
          Documents
        </h3>
        {hasAnyDocuments() ? (
          <div className="flex flex-wrap gap-2">
            {(candidate.documents?.resume?.url || candidate.resumeUrl) && (
              <button
                type="button"
                onClick={() => openDocumentPreview(
                  candidate.documents?.resume?.url || candidate.resumeUrl, 
                  'resume.pdf'
                )}
                className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
              >
                Resume/CV
              </button>
            )}
            {candidate.documents?.combinedDocuments?.url && (
              <button
                type="button"
                onClick={() => openDocumentPreview(
                  candidate.documents.combinedDocuments.url, 
                  'combined-documents.pdf'
                )}
                className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
              >
                Combined Docs
              </button>
            )}
            {candidate.additionalDocuments && candidate.additionalDocuments.map((doc, index) => (
              <button
                type="button"
                key={index}
                onClick={() => openDocumentPreview(
                  doc.documentUrl, 
                  doc.documentName || `document-${index + 1}.pdf`
                )}
                className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
              >
                {doc.documentName || `Doc ${index + 1}`}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-secondary">No documents uploaded yet</p>
        )}
      </div>

      {/* Document Preview Modal */}
      {documentPreview.isOpen && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.6)] flex items-center justify-center z-[1100] p-4"
          onClick={closeDocumentPreview}
        >
          <div
            className="bg-white rounded-xl w-full max-w-5xl h-[88vh] shadow-[0_12px_40px_rgba(0,0,0,0.25)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="m-0 text-sm font-semibold text-primary truncate pr-4">
                {documentPreview.title}
              </h3>
              <div className="flex items-center gap-2">
                <a
                  href={documentPreview.rawUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-1.5 px-3 rounded-md text-xs font-semibold bg-gray-100 text-primary no-underline hover:bg-gray-200"
                >
                  Open in New Tab
                </a>
                <button
                  type="button"
                  onClick={closeDocumentPreview}
                  className="py-1.5 px-3 rounded-md text-xs font-semibold bg-accent-green text-white hover:bg-[#5a6b4d]"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="h-[calc(88vh-56px)] bg-gray-50">
              {documentPreview.previewType === 'image' && (
                <div className="w-full h-full overflow-auto flex items-center justify-center bg-white">
                  <img 
                    src={documentPreview.previewUrl} 
                    alt={documentPreview.title} 
                    className="max-w-full max-h-full object-contain" 
                  />
                </div>
              )}
              {(documentPreview.previewType === 'pdf' || documentPreview.previewType === 'office') && (
                <iframe
                  src={documentPreview.previewUrl}
                  title={documentPreview.title}
                  className="w-full h-full border-0"
                />
              )}
              {documentPreview.previewType === 'unsupported' && (
                <div className="w-full h-full flex items-center justify-center text-center px-6">
                  <div>
                    <p className="text-sm text-text-secondary mb-3">
                      Preview is not available for this file type.
                    </p>
                    <a
                      href={documentPreview.rawUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block py-2 px-4 rounded-md text-xs font-semibold bg-accent-green text-white no-underline hover:bg-[#5a6b4d]"
                    >
                      Open File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CandidateDocumentsDisplay;
